import React from "react"
import { Link } from "react-router-dom"
import { useAuth } from "../AuthContext";
import useLocalizedCopy from "../hooks/useLocalizedCopy";
import useReceivedEmailsCount from "../hooks/useReceivedEmailsCount";
import useEmailFollowUpEnabled from "../hooks/useEmailFollowUpEnabled";

// Define types for the props
interface SidebarItemProps {
  to: string;
  icon: string;  // Assuming the icon class names are strings
  title: string;
  badge?: number; // Optional badge count
}

interface SidebarGroupProps {
  heading?: string;
  items: SidebarItemProps[];
}

// SidebarItem Component
const SidebarItem: React.FC<SidebarItemProps> = ({ to, icon, title, badge }) => {
  return (
    <Link className="menu-item" to={to}>
      <div className="menu-link flex items-center grow cursor-pointer border border-transparent gap-[10px] pl-[10px] pr-[10px] py-[6px]" tabIndex={0}>
        <span className="menu-icon items-start text-gray-500 dark:text-gray-400 w-[20px]">
          <i className={`ki-filled ${icon} text-lg`}></i>
        </span>
        <span className="menu-title text-sm font-semibold text-gray-700 menu-item-active:text-primary menu-link-hover:!text-primary flex-1">
          {title}
        </span>
        {badge && badge > 0 && (
          <span className="badge badge-warning badge-sm">
            {badge}
          </span>
        )}
      </div>
    </Link>
  )
}

// SidebarGroup Component
const SidebarGroup: React.FC<SidebarGroupProps> = ({ heading, items }) => {
  return (
    <>
      {heading && (
        <div className="menu-item pt-2.25 pb-px">
          <span className="menu-heading uppercase pl-[10px] pr-[10px] text-2sm font-semibold text-gray-500">
            {heading}
          </span>
        </div>
      )}
      {items.map((item, index) => (
        <SidebarItem key={index} to={item.to} icon={item.icon} title={item.title} badge={item.badge} />
      ))}
    </>
  )
}

// Sidebar Component
const Sidebar: React.FC = () => {
  const { copy } = useLocalizedCopy()
  const { unreadCount } = useReceivedEmailsCount()
  const { emailFollowUpEnabled } = useEmailFollowUpEnabled()

  const {
    viewingAllAccounts,
    currentUser
  } = useAuth()

  const dashboardItems: SidebarItemProps[] = [
    // Only show Welcome when viewing a specific account
    !viewingAllAccounts && { to: "/welcome", icon: "ki-home", title: "Welcome" },
    // { to: "/dashboard", icon: "ki-element-11", title: copy.Common.navigation.dashboard },
    { to: "/signatures/list", icon: "ki-brush", title: copy.Common.navigation.signatures },
  ].filter(Boolean)

  const serviceItems: SidebarItemProps[] = [
    { to: "/services/list", icon: "ki-questionnaire-tablet", title: copy.Services.list.title },
    !viewingAllAccounts && { to: "/create-service", icon: "ki-plus", title: copy.Services.create.title },
  ].filter(Boolean)

  const bookItems: SidebarItemProps[] = [
    { to: "/books", icon: "ki-book", title: copy.Books.viewBooks },
    !viewingAllAccounts && { to: "/create-book", icon: "ki-plus", title: copy.Books.createBook },
  ].filter(Boolean)

  const ipadItems: SidebarItemProps[] = [
    { to: "/ipads", icon: "ki-tablet", title: copy.IPads.list.title },
    !viewingAllAccounts && { to: "/add-ipad", icon: "ki-plus", title: copy.IPads.add.title },
  ].filter(Boolean)

  const manageCurrentAccountItems: SidebarItemProps[] = [
    { to: "/manage-account/details", icon: "ki-briefcase", title: copy.Account.details.title },
    // { to: "/manage-account/users", icon: "ki-users", title: copy.Account.users.title },
  ]

  // Marketing items logic:
  // - Follow-up Messages: Show when viewing all accounts OR when viewing a specific account
  // - Received Emails: Show when viewing all accounts OR when email follow-up is enabled for specific account
  // - QR Codes: Show when viewing a specific account
  const marketingItems: SidebarItemProps[] = [
    { to: "/follow-up-messages/review", icon: "ki-sms", title: copy.FollowUpMessages.title },
    (viewingAllAccounts || emailFollowUpEnabled) && { to: "/emails/received/all", icon: "ki-message-text-2", title: "Received Emails", badge: unreadCount },
    !viewingAllAccounts && { to: "/qr-codes", icon: "ki-barcode", title: "QR Codes" },
  ].filter(Boolean)

  const managementItems: SidebarItemProps[] = [
    { to: "/management/accounts/list", icon: "ki-briefcase", title: copy.Management.viewAccounts },
    { to: "/management/add-account", icon: "ki-plus", title: copy.Management.addAccount },
    { to: "/management/users", icon: "ki-users", title: copy.Management.viewUsers },
    { to: "/management/add-user", icon: "ki-plus", title: copy.Account.addUser.title },
  ] // See ManageAccountsRoutes.tsx for the routes

  const superSuperUserItems: SidebarItemProps[] = [
    { to: "/admin/agencies", icon: "ki-briefcase", title: copy.Admin.viewAgencies },
    { to: "/admin/add-agency", icon: "ki-plus", title: copy.Admin.addAgency },
    { to: "/admin/manage-crops", icon: "ki-frame", title: copy.Admin.manageCrops },
    { to: "/admin/generated-pdfs", icon: "ki-size", title: copy.Admin.generatedPDFs },
    // Only show System Settings and Transcription Logs to chris@cohoat.tech
    ...(currentUser.email === 'chris@cohoat.tech' ? [
      { to: "/admin/system-settings", icon: "ki-setting-2", title: copy.Admin.systemSettings },
      { to: "/admin/transcription-logs", icon: "ki-notepad", title: "Transcription Logs" },
    ] : []),
  ]

  return (
    <div
      className="sidebar dark:bg-coal-600 bg-light border-r border-r-gray-200 dark:border-r-coal-100 fixed top-0 bottom-0 z-20 hidden lg:flex flex-col shrink-0"
      style={{ height: '100vh', overflow: 'hidden', width: '250px' }}
      data-drawer="true"
      data-drawer-class="drawer drawer-start top-0 bottom-0"
      data-drawer-enable="true|lg:false"
      id="sidebar"
    >
      <div
        className="sidebar-header hidden lg:flex items-center relative justify-between px-3 lg:px-4 shrink-0 py-2 mb-1 border-b border-gray-200 dark:border-coal-100"
        id="sidebar_header"
      >
        <Link className="dark:hidden" to="/">
          <img
            className="default-logo min-h-[22px] max-h-[50px] max-w-100"
            src="/design/web_logo_signature_guestbook_black.png"
            alt="Logo"
          />
          <img
            className="small-logo min-h-[22px] max-h-[50px] max-w-100"
            src="/design/web_logo_signature_guestbook_black.png"
            alt="Small Logo"
          />
        </Link>
        <Link className="hidden dark:block" to="/">
          <img
            className="default-logo min-h-[22px] max-h-[50px] max-w-100"
            src="/design/web_logo_signature_guestbook_black.png"
            alt="Logo"
          />
          <img
            className="small-logo min-h-[22px] max-h-[50px] max-w-100"
            src="/design/web_logo_signature_guestbook_black.png"
            alt="Small Logo"
          />
        </Link>
      </div>
      <div
        className="sidebar-content flex flex-col flex-1 py-1 pr-2 overflow-hidden"
        id="sidebar_content"
      >
        <div
          className="flex-1 pl-2 lg:pl-5 pr-1 lg:pr-3 overflow-y-auto overflow-x-hidden"
          style={{ height: 'calc(100vh - 100px)' }}
          id="sidebar_scrollable"
        >
          <div
            className="menu flex flex-col grow gap-0.5 pb-8"
            data-menu="true"
            data-menu-accordion-expand-all="false"
            id="sidebar_menu"
          >
            <SidebarGroup items={dashboardItems} />
            <SidebarGroup heading={copy.Common.navigation.services} items={serviceItems} />
            {/* This has been deprecated in favor of service-detail book management */}
            {process.env.NODE_ENV === 'development' && false && [
              'chris@cohoat.tech',
              'chris@indianapolis.tech',
            ].includes(currentUser.email.toLowerCase()) && <>
                {!viewingAllAccounts && <>
                  <SidebarGroup heading={copy.Books.title} items={bookItems} />
                </>}
              </>}
            {!viewingAllAccounts && <SidebarGroup heading={copy.Common.navigation.ipads} items={ipadItems} />}
            {marketingItems.length > 0 && <SidebarGroup heading={copy.Common.navigation.marketing} items={marketingItems} />}
            {/* Account section available to all users when viewing a specific account */}
            {!viewingAllAccounts && <SidebarGroup heading={copy.Common.navigation.account} items={manageCurrentAccountItems} />}
            {(currentUser.is_agency_admin || currentUser.is_super_super_user) && <SidebarGroup heading={copy.Management.title} items={managementItems} />}
            {currentUser.is_super_super_user && <SidebarGroup heading={copy.Admin.title} items={superSuperUserItems} />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Sidebar
